import { isDefined } from '@app/helpers/kindOf';
import { ScheduleAvailabilityTypeEnum, IBase, IPack, ISchedule, slugify } from '@app/common';
import { UseFormReturn } from 'react-hook-form';
import { sansLesClesPriveesDeCopie } from '../Duplication/cles-privees';
export interface PackFormValues extends Partial<IPack> {
  image: any;
}

export const transformPackFormValuesToPayload = (values: Omit<Partial<IPack & { image: any }>, keyof IBase>) => {
  // `copieDe` et `imagesDeLOriginal` ne servent qu'au panneau de création ; `copieDesImagesDe` part au serveur.
  const { catalogToPacks, ...data } = sansLesClesPriveesDeCopie(values);

  return {
    ...data,
    name: data.name,
    image: data.image || null,
    tags: data.tags || [],
    slug: slugify(data.name!),
    description: data.description,
    accountCode: data.accountCode || null,
    disabled: data.disabled,
    price: data.price,
    tax: data.tax,
    takeAwayPrice: data.takeAwayPrice || null,
    takeAwayTax: data.takeAwayTax || null,
    deliveryPrice: data.deliveryPrice || null,
    deliveryTax: data.deliveryTax || null,
    onSpotPrice: data.onSpotPrice || null,
    onSpotTax: data.onSpotTax || null,
    orderModes: data.orderModes || [],
    packToProductVariations: data.packToProductVariations || [],
    packToSteps: data.packToSteps || [],
    availabilityType: data.availabilityType || null,
    availabilitySchedule: values.availabilityType === ScheduleAvailabilityTypeEnum.specificDays ? data.availabilitySchedule : {} as ISchedule,
  }
}

export const usePackFormSubmit = () => {
  const objectValuesKeys = ['accountCode', 'tax', 'takeAwayTax', 'deliveryTax', 'onSpotTax'];

  return async (formMethods: UseFormReturn<any, any>, submitCb: (payload: FormData) => void) => {
    const values = formMethods.getValues();

    const payload = transformPackFormValuesToPayload(values);

    const formData = new FormData();

    for (const [key, value] of Object.entries(payload)) {
      if (objectValuesKeys.includes(key)) {
        if (!isDefined(value)) {
          continue;
        }

        for (let [nestedKey, nestedValue] of Object.entries(value) as any) {
          if (isDefined(nestedValue)) {
            formData.append(`${key}[${nestedKey}]`, nestedValue as string);
          }
        }
      } else if (key === 'availabilitySchedule' && typeof value === 'object' && value !== null) {
        formData.append(`availabilitySchedule`, JSON.stringify(value));
      } else if (value === undefined) {
        // `FormData.append(key, undefined)` enverrait la chaîne « undefined ».
        continue;
      } else {
        formData.append(key, Array.isArray(value) ? JSON.stringify(value) : (value as string));
      }
    }

    const isValid = await formMethods.trigger();

    if (isValid) {
      submitCb(formData);
    }
  };
};

export const transformToUpdatableFormPack = (pack?: Partial<IPack>): PackFormValues => {
  return {
    name: pack?.name || '',
    image: null,
    description: pack?.description || '',
    accountCode: pack?.accountCode,
    disabled: pack?.disabled,
    price: pack?.price,
    tax: pack?.tax,
    tags: pack?.tags,
    catalogToPacks: pack?.catalogToPacks ?? [],
    takeAwayPrice: pack?.takeAwayPrice,
    takeAwayTax: pack?.takeAwayTax,
    deliveryPrice: pack?.deliveryPrice,
    deliveryTax: pack?.deliveryTax,
    onSpotPrice: pack?.onSpotPrice,
    onSpotTax: pack?.onSpotTax,
    orderModes: pack?.orderModes,
    packToProductVariations: pack?.packToProductVariations,
    packToSteps: pack?.packToSteps,
    availabilityType: pack?.availabilitySchedule && Object.keys(pack.availabilitySchedule).length > 0 && Object.values(pack.availabilitySchedule).some((day) => day.length > 0) ? ScheduleAvailabilityTypeEnum.specificDays : ScheduleAvailabilityTypeEnum.always,
    availabilitySchedule: pack?.availabilitySchedule,
  }
}
